import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  useKivunovaTranslation,
  PhoneCountryCodeMap,
  combinePhoneNumber,
  formatCompanyAddressForDisplay,
  translateCompanyCountry
} from "@kivunova/kivufrontendcommon";
import { getOrganizationLabelsForBusinessType } from "../../hooks/useOrganizationLabels";
import {
  AdminInfo,
  CompanyInfo,
  RegistrationData
} from "./types/registrationTypes";
import { BRAND_COLOR } from "@kivunova/kivufrontendcommon";
import {
  formatVatCalculationModeDisplay,
  formatVatPercentageDisplay
} from "../Company/utils/companyVatDisplay";
import { VatCalculationMode } from "../Company/constants/companyVatConstants";

interface ReviewSubmitProps {
  allData: Partial<RegistrationData>;
}

const ReviewAndSubmit: React.FC<ReviewSubmitProps> = ({ allData }) => {
  const { t } = useKivunovaTranslation();
  const orgLabels = getOrganizationLabelsForBusinessType(
    t,
    allData.company?.businessType
  );

  const formatAddress = (
    address: Parameters<typeof formatCompanyAddressForDisplay>[0]
  ) =>
    formatCompanyAddressForDisplay(address, (country) =>
      translateCompanyCountry(country, t)
    );

  const renderSection = (
    data: AdminInfo | CompanyInfo | undefined,
    chipLabel: string,
    defaultExpanded?: boolean
  ) => {
    if (!data) return null;

    const entries = Object.entries(data);

    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          mb: 2,
          "&:before": { display: "none" }
        }}
      >
        {/* Accordion Header */}
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: BRAND_COLOR }} />}
          sx={{
            backgroundColor: "#fafafa",
            borderBottom: "1px solid rgba(0,0,0,0.05)"
          }}
        >
          <Typography variant="body2" fontWeight={550}>
            {chipLabel}
          </Typography>
        </AccordionSummary>

        {/* Accordion Content */}
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              overflow: "hidden",
              border: "none",
              padding: "4px"
            }}
          >
            <Table
              size="small"
              sx={{
                "& td": {
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                  py: 1.2,
                  px: { xs: 1, sm: 2 }
                },
                "& tr:last-child td": {
                  borderBottom: "none"
                }
              }}
            >
              <TableBody>
                {entries.map(([key, value]) => {
                  // Skip phoneCountryCode as we'll combine it with phone
                  if (key === "phoneCountryCode") {
                    return null;
                  }

                  const label = t(
                    `registration-${key}`,
                    key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (s) => s.toUpperCase())
                  );

                  let displayValue: string | React.ReactNode = "N/A";

                  if (key === "password") {
                    displayValue = "********";
                  } else if (key === "vatPercentage") {
                    displayValue =
                      formatVatPercentageDisplay(value as number) ?? "N/A";
                  } else if (key === "vatCalculationMode") {
                    displayValue =
                      formatVatCalculationModeDisplay(
                        value as VatCalculationMode,
                        t
                      ) ?? "N/A";
                  } else if (key === "phone" && "phoneCountryCode" in data) {
                    // Combine phone with country code for display
                    const adminData = data as AdminInfo;
                    displayValue = combinePhoneNumber(
                      adminData.phoneCountryCode,
                      adminData.phone,
                      PhoneCountryCodeMap
                    );
                  } else if (key === "address" && typeof value === "object") {
                    displayValue = formatAddress(value);
                  } else if (
                    typeof value === "string" ||
                    typeof value === "number"
                  ) {
                    displayValue = String(value);
                  }

                  return (
                    <TableRow key={key}>
                      <TableCell
                        sx={{
                          width: "40%",
                          fontWeight: 500,
                          color: "text.secondary",
                          borderRight: "1px solid rgba(0,0,0,0.05)"
                        }}
                      >
                        {label}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          wordBreak: "break-word",
                          color: "text.primary",
                          fontWeight: 500
                        }}
                      >
                        {displayValue}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3 },
        maxWidth: 800,
        mx: "auto",
        width: "100%"
      }}
    >
      {renderSection(
        allData.admin,
        t("personal-information-chip-label", "Personal Info"),
        true
      )}
      {renderSection(allData.company, orgLabels.reviewSectionChip)}
      {orgLabels.registration.showStoreOnboardingHint && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, textAlign: "center" }}
        >
          {t(
            "registration-add-store-later-hint",
            "You can add your first store after you sign in from the Stores page in the sidebar."
          )}
        </Typography>
      )}
    </Box>
  );
};

export default ReviewAndSubmit;
